import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { ApiError, api } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { GuestPicker } from "./GuestPicker";
import { useAddressSuggestions } from "./useAddressSuggestions";
import { salesApi } from "../../lib/sales-api";
import type { SalesMeetingMode } from "../../lib/sales-types";
import { Button } from "../ui/Button";
import { Overlay } from "../ui/Overlay";
import { cn } from "../ui/cn";
import { Chip } from "../sales/NewCallDialog";

/**
 * Book a meeting.
 *
 * The same shape as raising a call, and deliberately so: company as free text
 * with CRM matches offered underneath, contacts as buttons once an account is
 * linked, everything else a single tap. A rep books these standing in a car
 * park with a phone in one hand, and a form of dropdowns is a form of two-step
 * interactions where the second step is where people give up and put it in
 * their own calendar instead.
 *
 * Two fields exist here that the call form has no use for. **When** is the
 * whole point of a diary and is the only required field beyond the company.
 * **Where** is what a field rep is actually reading this record for later, so
 * it sits directly under the time rather than among the optional extras.
 *
 * Duration is offered as buttons rather than an end-time picker. Nobody types
 * "15:30" as an end; they think "an hour", and three taps of a time control to
 * express that is how the field ends up empty.
 */
export function NewMeetingDialog({
  onClose,
  currentUserId,
  /** Pre-linked when booked from a CRM company or from a call. */
  company,
  /** The call this came out of, when it came out of one. */
  call,
  /**
   * When to book it for, when it was started from a slot on the calendar --
   * a click is an hour from where you clicked, a drag is exactly what you
   * dragged out, as in Google Calendar.
   */
  slot,
}: {
  onClose: () => void;
  currentUserId: number;
  company?: { id: number | null; name: string };
  call?: { id: number; contactId: number | null };
  slot?: { start: Date; minutes: number };
}) {
  const navigate = useNavigate();
  const here = useLocation();
  const client = useQueryClient();

  const [companyName, setCompanyName] = useState(company?.name ?? "");
  const [companyId, setCompanyId] = useState<number | null>(
    company?.id ?? null,
  );
  const [contactId, setContactId] = useState<number | null>(
    call?.contactId ?? null,
  );
  const [subject, setSubject] = useState("");
  const [mode, setMode] = useState<SalesMeetingMode>("in_person");
  const [location, setLocation] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  /* Everybody outside NetXL to invite, as addresses. Empty to start with:
     inviting somebody is always a thing you did, never a thing that happened
     because you picked a contact. */
  const [guests, setGuests] = useState<string[]>([]);
  const [details, setDetails] = useState("");
  const [startAt, setStartAt] = useState(() => asLocalInput(slot?.start ?? new Date()));
  const [minutes, setMinutes] = useState<number | null>(slot?.minutes ?? 60);
  /* Who from NetXL is going. You, to start with, because you nearly always
     are -- and because a form that opens with nobody on it is a form somebody
     submits with nobody on it. */
  const [attendees, setAttendees] = useState<number[]>([currentUserId]);

  // Escape closes, matching every other overlay in the app.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* Matches for what has been typed, offered but never imposed. Disabled once
     an account is linked -- the box then holds a chosen name. */
  const matches = useQuery({
    queryKey: queryKeys.crmSearch(companyName),
    queryFn: () => crmApi.search(companyName),
    enabled: companyName.trim().length >= 2 && companyId === null,
    staleTime: 30_000,
  });

  const contacts = useQuery({
    queryKey: ["crm", "contacts", companyId],
    queryFn: () => crmApi.contacts(companyId!),
    enabled: companyId !== null,
    staleTime: 60_000,
  });

  /* Only a manager can put a meeting in somebody else's diary, so only a
     manager needs the list of whose. */
  /* Everybody, not just managers: anybody may put a colleague on a visit. */
  const users = useQuery({
    queryKey: queryKeys.users,
    queryFn: api.users,
    staleTime: 5 * 60_000,
  });

  const google = useQuery({
    queryKey: queryKeys.googleConnection,
    queryFn: api.googleConnection,
    staleTime: 5 * 60_000,
  });

  /*
   * Whether this meeting will be filed in Google, which is simply whether
   * Google is connected.
   *
   * There used to be a tick box. It defaulted off, which meant the ordinary
   * way to book a meeting was to book one that never reached the calendar of
   * the person who booked it -- and a diary entry only Pulse knows about is
   * one nobody is reminded of by the thing they actually live in. Connecting
   * Google is already the deliberate act; being asked again per meeting was
   * asking the same question twice and taking the wrong answer by default.
   */
  const calendarReady =
    google.data?.connected === true &&
    google.data.capabilities.includes("calendar");
  const addToCalendar = calendarReady;

  const create = useMutation({
    mutationFn: () =>
      salesApi.createMeeting({
        companyName: companyName.trim(),
        companyId,
        contactId,
        callId: call?.id ?? null,
        subject: subject.trim() || null,
        mode,
        /* Both are sent whichever is showing. Somebody who booked an address,
           switched to online and switched back should find their address still
           there -- and a meeting that moves online next week keeps the place it
           was going to be. */
        location: location.trim() || null,
        meetingUrl: meetingUrl.trim() || null,
        details: details.trim() || null,
        startAt: new Date(startAt).toISOString(),
        endAt:
          minutes === null
            ? null
            : new Date(
                new Date(startAt).getTime() + minutes * 60_000,
              ).toISOString(),
        attendeeUserIds: attendees,
        addToCalendar,
        /* Only sent when the calendar entry is being made, because Google is
           the only thing that would email them. Storing a guest list nobody
           will ever be sent would be a list of people you believe you invited
           and did not. */
        guests: addToCalendar ? guests : [],
      }),
    onSuccess: (meeting) => {
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
      onClose();
      /* Opened over whatever view it was booked from, which keeps the
         calendar where it was -- see `MeetingsHome`. */
      navigate(
        `/meetings/${meeting.id}${here.pathname.startsWith("/meetings") ? here.search : ""}`,
      );
    },
  });

  const people = contacts.data ?? [];

  /**
   * Pick, or unpick, the person we are going to see.
   *
   * Picking somebody invites them. It used to only file the write-up against
   * them, with a line underneath explaining that inviting was a separate job
   * further down the form -- which is a caption asking the reader to do the
   * obvious thing manually. Naming the person you are meeting and then not
   * inviting them is not a case worth protecting; it is the mistake the guest
   * list was added to stop.
   *
   * Unpicking takes them back off, so the pair stays reversible. Somebody who
   * picked the wrong contact should not have to find and delete a chip they
   * never typed. An address they added by hand that happens to match is lost
   * in that case, which is the one accepted cost -- they can add it back, and
   * it is on screen the whole time.
   */
  const chooseContact = (next: { id: number; email: string } | null): void => {
    const previous = people.find((entry) => entry.id === contactId);
    setContactId(next?.id ?? null);

    setGuests((current) => {
      const without = previous
        ? current.filter(
            (email) => email !== previous.email.trim().toLowerCase(),
          )
        : current;

      if (!next) return without;

      const email = next.email.trim().toLowerCase();
      return without.includes(email) ? without : [...without, email];
    });
  };

  return (
    /*
       The actions are the frame's, not the form's, so they stay on screen
       while the fields scroll. This form is taller than a handset either way
       -- six fields and two chip rows -- and when the card was free to grow,
       Cancel and Book it sat below the bottom edge of a page that did not
       scroll. `form=` is what keeps the submit button attached to a form it is
       no longer inside.
    */
    <Overlay
      title="New meeting"
      size="md"
      onClose={onClose}
      footer={
        <>
          {create.isError ? (
            <p className="mr-auto text-xs text-red-600">
              {create.error instanceof ApiError
                ? create.error.message
                : "Could not book that meeting."}
            </p>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            disabled={!companyName.trim() || !startAt || create.isPending}
          >
            {create.isPending ? "Booking…" : "Book it"}
          </Button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (companyName.trim() && startAt) create.mutate();
        }}
      >
        <Field label="Company">
          <input
            autoFocus
            value={companyName}
            onChange={(event) => {
              setCompanyName(event.target.value);
              /* Typing over a linked name unlinks it, and takes the chosen
                 person with it -- they belong to the old account. */
              setCompanyId(null);
              setContactId(null);
            }}
            placeholder="Who are we seeing?"
            className={INPUT}
          />
        </Field>

        {companyId !== null ? (
          <p className="-mt-2 text-[11px] text-faint">
            Linked to the CRM account — the write-up will appear in their
            activity, and you can raise tasks from this meeting.
          </p>
        ) : null}

        {companyId === null && (matches.data?.results.length ?? 0) > 0 ? (
          <div className="rounded-md border border-line">
            <p className="border-b border-line px-2 py-1 text-[11px] text-faint">
              Link to an existing account?
            </p>
            <ul className="max-h-40 overflow-y-auto">
              {matches.data!.results.slice(0, 6).map((result) => (
                <li key={result.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setCompanyId(result.id);
                      setCompanyName(result.name);
                    }}
                    className="flex w-full items-baseline gap-2 px-2 py-1.5 text-left hover:bg-surface-2"
                  >
                    <span className="truncate text-xs text-content">
                      {result.name}
                    </span>
                    <span className="truncate text-[11px] text-faint">
                      {result.domain ?? result.city ?? ""}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <Field label="When">
          <input
            type="datetime-local"
            value={startAt}
            onChange={(event) => setStartAt(event.target.value)}
            className={INPUT}
          />
        </Field>

        {/*
          How long, as buttons. Nobody thinks "ends at 15:30" -- they think
          "an hour" -- and expressing that through a second time picker is
          three interactions for one thought, which is how an end time ends
          up never being filled in. "Open" is honest about the visit that is
          as long as it takes.
        */}
        <Field label="How long">
          <div className="flex flex-wrap gap-1.5">
            {/* A dragged-out length that is none of the presets, shown as
                itself so the chips do not claim it was something else. */}
            {minutes !== null && !DURATIONS.some((option) => option.minutes === minutes) ? (
              <Chip on onClick={() => undefined}>
                {minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ""}`}
              </Chip>
            ) : null}
            {DURATIONS.map((option) => (
              <Chip
                key={option.label}
                on={minutes === option.minutes}
                onClick={() => setMinutes(option.minutes)}
              >
                {option.label}
              </Chip>
            ))}
          </div>
        </Field>

        {/*
          In person or online, as a toggle rather than something inferred from
          whether the text starts with "http".

          The two branches want genuinely different things from whoever is
          booking -- an address to put in a satnav, or a link to click -- and
          asking the question outright is what lets the row, the detail page
          and the CRM write-up each say the right thing afterwards. Guessing
          would be right most of the time, which is the worst kind of right
          for something a rep plans a morning around.
        */}
        <Field label="Where">
          <div className="mb-2 flex flex-wrap gap-1.5">
            <Chip on={mode === "in_person"} onClick={() => setMode("in_person")}>
              In person
            </Chip>
            <Chip on={mode === "online"} onClick={() => setMode("online")}>
              Online
            </Chip>
          </div>

          {mode === "in_person" ? (
            <AddressBox value={location} onChange={setLocation} />
          ) : (
            <>
              {/*
                Online means a Google Meet link, made for you -- nobody should
                have to go to Google, make one and paste it back. The box stays
                for the meeting somebody else is hosting on Teams or Zoom, and
                anything pasted into it wins over a new Meet link.
              */}
              <input
                type="url"
                value={meetingUrl}
                onChange={(event) => setMeetingUrl(event.target.value)}
                placeholder={
                  calendarReady
                    ? "A Google Meet link is made for you — or paste a Teams/Zoom link"
                    : "Paste the Meet or Teams link"
                }
                className={INPUT}
              />
              <p
                className={cn(
                  "mt-1.5 text-[11px]",
                  calendarReady || meetingUrl.trim()
                    ? "text-faint"
                    : "text-amber-700 dark:text-amber-400",
                )}
              >
                {meetingUrl.trim()
                  ? "Using the link you pasted."
                  : calendarReady
                    ? "A Google Meet link will be created when you book it."
                    : "Connect Google Calendar under Your settings and a Meet link is made for you."}
              </p>
            </>
          )}
        </Field>

        {/*
          Who it is with, and who is invited, together.

          One question in two parts -- "which of their people am I meeting"
          and "who else needs to be in the room" -- so they sit next to each
          other. They used to be separated by the title, the notes and the
          NetXL attendee list, which is why the first needed a caption
          pointing at the second.
        */}
        {companyId !== null && people.length > 0 ? (
          <Field label="Who we are seeing">
            <div className="flex flex-wrap gap-1.5">
              {people.map((contact) => (
                <Chip
                  key={contact.id}
                  on={contactId === contact.id}
                  onClick={() =>
                    chooseContact(
                      contactId === contact.id ? null : contact,
                    )
                  }
                >
                  {contact.name ?? contact.email}
                  {contact.role ? (
                    <span className="ml-1 opacity-70">({contact.role})</span>
                  ) : null}
                </Chip>
              ))}
            </div>

            {/* What picking one just did, stated where it happened rather
                than as a caption pointing somewhere else. */}
            {contactId !== null ? (
              <p className="mt-1.5 text-[11px] text-faint">
                {addToCalendar
                  ? "Invited below, and the write-up is filed against them."
                  : "The write-up is filed against them. Connect Google to invite them too."}
              </p>
            ) : null}
          </Field>
        ) : null}

        {addToCalendar ? (
          <Field label="Guests">
            <GuestPicker
              companyId={companyId}
              value={guests}
              onChange={setGuests}
            />

            <p className="mt-1.5 text-[11px] text-faint">
              {guests.length === 0
                ? "Nobody outside NetXL will be told about this."
                : `Google will email ${guests.length === 1 ? "them" : "them all"} an invitation when you book it.`}
            </p>
          </Field>
        ) : null}

        {/*
          A meeting genuinely has a title, unlike a call -- there are no group
          buttons here to say what kind of thing this is, and "why am I
          driving to Rotherham" is the first question the record has to answer
          when it is read back a week later. Named "Title" because that is the
          line that would head the event in a calendar, which is what people
          expect to be typing.
        */}
        <Field label="Title">
          <input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="Site survey, quote review, first visit…"
            className={INPUT}
          />
        </Field>

        <Field label="Notes">
          <textarea
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            rows={3}
            placeholder="Anything to read on the way"
            className={cn(INPUT, "resize-y")}
          />
        </Field>

        {/*
          Who from NetXL is going, as toggles rather than one dropdown.

          Multiple, because a visit routinely takes two people -- the account
          manager and whoever knows the product -- and a single owner meant
          that second person either did not appear at all or had the meeting
          duplicated into their diary. Both happened.

          Not a manager's privilege, unlike assigning a call: putting a
          colleague on a visit is how field sales works, and they can take
          themselves off again.
        */}
        <Field label="NetXL attendees">
          <div className="flex flex-wrap gap-1.5">
            {(users.data ?? []).map((person) => (
              <Chip
                key={person.id}
                on={attendees.includes(person.id)}
                onClick={() =>
                  setAttendees((was) =>
                    was.includes(person.id)
                      ? was.filter((id) => id !== person.id)
                      : [...was, person.id],
                  )
                }
              >
                {person.id === currentUserId
                  ? `${person.name} (you)`
                  : person.name}
              </Chip>
            ))}
          </div>
        </Field>

        {/*
          No tick box.

          Connecting Google is the decision, and it has already been made by
          anybody who reaches this branch. Asking again per meeting asked the
          same question twice and took the wrong answer by default: it
          defaulted off, so the ordinary way to book a meeting was to book one
          that never reached the calendar of the person booking it -- and a
          diary entry only Pulse knows about is one nobody gets reminded of by
          the thing they actually live in.

          What is left here is the statement of what booking will do. The
          guest list itself has moved up beside "Who we are seeing", where the
          question it answers is actually being asked.
        */}
        {addToCalendar ? (
          <p className="text-xs text-muted">
            This goes in your Google calendar.
          </p>
        ) : google.data?.available ? (
          <p className="text-[11px] text-faint">
            Connect Google under Your settings to put meetings in your own
            calendar and send invitations.
          </p>
        ) : null}

      </form>
    </Overlay>
  );
}

/**
 * The location box, with suggestions offered underneath.
 *
 * Offered, never imposed: the box is ordinary free text and anything typed into
 * it is kept. Half of these are "their Rotherham branch", which no address API
 * will ever suggest and which is a perfectly good answer -- so the suggestions
 * sit below the field the way the CRM matches do on the company box, rather
 * than replacing it with a picker.
 */
function AddressBox({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [picked, setPicked] = useState(false);
  const suggestions = useAddressSuggestions(picked ? "" : value);

  return (
    <>
      <input
        value={value}
        onChange={(event) => {
          setPicked(false);
          onChange(event.target.value);
        }}
        placeholder="Their office, or a postcode"
        className={INPUT}
      />

      {suggestions.length > 0 ? (
        <div className="mt-1 rounded-md border border-line">
          <ul className="max-h-40 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <li key={suggestion}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(suggestion);
                    /* Stops the list re-appearing under the value it just put
                       in the box, which would offer to pick what is picked. */
                    setPicked(true);
                  }}
                  className="w-full px-2 py-1.5 text-left text-xs text-content hover:bg-surface-2"
                >
                  {suggestion}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

const DURATIONS: readonly { label: string; minutes: number | null }[] = [
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "Half a day", minutes: 240 },
  { label: "Open", minutes: null },
];

/** Ties the footer's submit button to the form it now sits outside of. */
const FORM_ID = "new-meeting";

const INPUT =
  "w-full rounded-md border border-line bg-surface px-2.5 py-1.5 text-sm text-content placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40";

/**
 * A moment as a `datetime-local` value -- the calendar slot it was started
 * from, or now.
 *
 * Now rather than a guess at a future slot when there is no slot: whatever it
 * is set to is a value somebody has to change anyway, and a form that opens on
 * the present moment is one where every edit is a nudge forward from a known
 * point rather than a correction of somebody else's assumption. It also makes
 * "we are in this meeting right now" a two-second booking.
 *
 * Built from local parts rather than `toISOString()`, which converts to UTC and
 * would offer an hour earlier for five months of the year.
 */
function asLocalInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-faint">
        {label}
      </span>
      {children}
    </label>
  );
}
